import { useEmployeeStore } from "@/store/pinia/employee";
import { getCurrentInstance, nextTick } from "vue";
import { usePositionStore } from "@/store/pinia/position";
import { useEmployeeFilters } from "@/composables/employees/useEmployeeFilters";
import {
  addPositionAssignment,
  updatePositionAssignment,
  createEmployee,
  createUser,
  deleteEmployee,
  archiveEmployee,
  unArchiveEmployee,
  // getListofUser,
  reactivateEmployee,
  removePositionAssignmentForEmployee,
  terminateEmployee,
  updateEmployee,
  userCheckEmail,
  activateEmployee,
  getChatterList,
  getAccountData,
  updateEmployeeExcludeValue,
  getEmployeeByIdV2,
  getEmployeeAssignments,
  updateImportanceValue,
} from "@/api";
import { computed } from "vue";
import { vacanciesMatch } from "@components/utils/primaryAssignmentPositions";

export function useEmployees() {
  const {
    setPaginationLimit,
    setPaginationOffset,
    setOrderingField,
    addFilter,
    removeFilter,
    clearDynamicFilters,
    dynamicFilters,
    defaultParams,
  } = useEmployeeFilters();
  const employeeStore = useEmployeeStore();
  const positionStore = usePositionStore();
  const app = getCurrentInstance();
  const { proxy } = app || {};

  // Persist ordering in localStorage and restore on init
  const EMPLOYEES_ORDERING_KEY = "employees.ordering";
  try {
    const savedOrdering =
      typeof localStorage !== "undefined" && localStorage.getItem(EMPLOYEES_ORDERING_KEY);
    if (savedOrdering) {
      setOrderingField(savedOrdering);
    }
  } catch (e) {
    /* ignore storage errors */
  }
  const allEmployees = computed(() => {
    return employeeStore.getEmployees;
  });

  const operations = {
    TERMINATE_EMPLOYEE: "terminate_employee",
    ACTIVATE_EMPLOYEE: "activate_employee",
    DELETE_EMPLOYEE: "delete_employee",
    REACTIVATE_EMPLOYEE: "reactivate_employee",
    EDIT_EMPLOYEE: "edit_employee",
    CHANGE_EMPLOYEE_PICTURE: "change_employee_picture",
    GIVE_ACCESS_TO_EMPLOYEE: "give_access_to_employee",
    ARCHIVE_EMPLOYEE: "archive_employee",
  };

  const setCurrentEmployee = (employee) => {
    employeeStore.setCurrentEmployee(employee);
  };

  const getCurrentEmployee = () => {
    return employeeStore.currentEmployee;
  };
  const loadAllEmployees = async (options = {}) => {
    try {
      if (proxy && proxy.$loader && typeof proxy.$loader.show === "function") {
        proxy.$loader.show();
      }
    } catch (e) {
      /* no-op */
    }
    await employeeStore.loadEmployees(
      {
        ...defaultParams.value,
        ...dynamicFilters.value,
      },
      options
    );
    try {
      if (proxy && proxy.$loader && typeof proxy.$loader.hide === "function") {
        proxy.$loader.hide();
      }
    } catch (e) {
      /* no-op */
    }
    await nextTick();
  };
  const loadAllEmployeesByBatches = async () => {
    await employeeStore.loadEmployeesBatch();
    employeeStore.fetchRemainingEmployeesInBatches().catch((e) => console.error(e));
  };
  const eligiblePositions = computed(() => {
    const currentEmployee = employeeStore.currentEmployee;
    if (!currentEmployee) return [];

    const employeeVacancy = currentEmployee.vacancy ?? currentEmployee.availability;
    const preparePositionIds = new Set(
      currentEmployee.preparing_for_positions?.map((pos) => pos.position) || []
    );

    return (positionStore.positions || []).filter(
      (el) =>
        vacanciesMatch(el?.vacancy, employeeVacancy) &&
        el.status == "active" &&
        !preparePositionIds.has(el.id)
    );
  });
  const eligiblePositionsForPrepare = computed(() => {
    if (!employeeStore.currentEmployee) return [];

    const allPositions = positionStore.positions || [];
    const currentEmployee = employeeStore.currentEmployee;

    // Collect the IDs of primary positions assigned to the current employee
    const currentEmployeePositionIds = new Set(
      currentEmployee.primary_positions?.map((pos) => pos.position) || []
    );

    // Filter all positions where status is 'active' or 'inactive', but not assigned to the current employee
    return allPositions.filter((position) => {
      const positionVacancy = (position?.capacity || 0) - (position?.allocation || 0);
      const isStatusValid = position.status === "active" || position.status === "inactive";
      const isNotAlreadyAssigned = !currentEmployeePositionIds.has(position.id);
      return isStatusValid && isNotAlreadyAssigned && positionVacancy >= 1;
    });
  });

  const getEmployeeById = async (accountId, employeeId) => {
    try {
      const response = await getEmployeeByIdV2({
        account_id: accountId,
        employeeId,
      });
      return response;
    } catch (e) {
      console.error(e);
    }
  };

  const getEmployeeAssignmentsByEmployeeId = async (accountId, employeeId) => {
    try {
      const response = await getEmployeeAssignments(accountId, employeeId);
      if (response?.data?.results) {
        return response?.data?.results;
      } else {
        return [];
      }
    } catch (e) {
      console.error("e: ");
    }
  };

  const saveEmployee = async ({ accountId, employee, allData }) => {
    try {
      if (employee && employee?.id) {
        let payload = {
          first_name: employee.first_name?.trim?.() ?? "",
          last_name: employee.last_name?.trim?.() ?? "",
          title: employee.title ?? allData?.title,
          email: employee.email || allData?.email || allData?.user?.email,
        };
        if (employee.avatar) {
          payload.avatar = employee.avatar;
        }
        await updateEmployee(accountId, employee.id, payload);
      } else {
        const data = {
          first_name: employee.first_name,
          last_name: employee.last_name,
          title: employee.title,
        };
        const newEmployee = await createEmployee(accountId, data);
        return newEmployee;
      }
    } catch (e) {
      console.log("e: ", e);
    }
  };

  const saveEmployeeWithAvatar = async ({ accountId, employee }) => {
    try {
      let payload = {
        first_name: employee.first_name,
        last_name: employee.last_name,
        title: employee.title,
        avatar: employee.avatar,
      };
      const response = await updateEmployee(accountId, employee.id, payload);
      return response;
    } catch (e) {
      console.log("e: ", e);
    }
  };

  const checkEmailUser = async ({ accountId, email }) => {
    const { data } = await userCheckEmail(accountId, { email });
    // const emailIsBlocked = data?.access;
    // const emailIsUsed = data?.user;
    // if (emailIsUsed) {
    //   const { data } = await getListofUser(accountId);
    //   const userList = data.results;
    //   const user = userList.find((el) => el.user.email == email);
    //   const existingUser = {
    //     firstName: user.user.first_name,
    //     lastName: user.user.last_name,
    //   };
    //   return {
    //     user: existingUser,
    //     data: "emailUsed",
    //   };
    // } else if (emailIsBlocked) {
    //   return {
    //     data: "emailBlocked",
    //   };
    // } else {
    //   return {
    //     data: "step2",
    //   };
    // }
    return data;
  };

  const giveAccessToEmployee = async ({ accountId, payload }) => {
    await createUser(accountId, payload);
  };

  const onUpdateAvailability = async ({ accountId, data }) => {
    try {
      const { id, first_name, last_name, title } = data.employee;
      const payload = {
        first_name,
        last_name,
        title,
        availability: data.value,
      };
      await updateEmployee(accountId, id, payload);
    } catch (e) {
      console.error("error: ", e);
    }
  };

  const setPaginationDataLimit = (limit) => {
    setPaginationLimit(limit);
  };

  const paginationDefaultParams = computed(() => defaultParams.value);

  const paginationLimit = computed(() => defaultParams.value.limit);
  const paginationOffset = computed(() => defaultParams.value.offset);
  const paginationOrdering = computed(() => defaultParams.value.ordering);

  const setPaginationDataOffset = (offset) => {
    setPaginationOffset(offset);
  };

  const setOrderingDataField = (ordering) => {
    setOrderingField(ordering);
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(EMPLOYEES_ORDERING_KEY, ordering);
      }
    } catch (e) {
      /* ignore storage errors */
    }
  };
  const addEmployeeFilter = (key, value) => {
    addFilter(key, value);
  };

  const removeEmployeeFilter = (key) => {
    removeFilter(key);
  };

  const getDynamicEmployeeFilters = () => {
    return dynamicFilters.value;
  };

  const clearEmployeeDynamicFilters = () => {
    clearDynamicFilters();
  };

  const onAssignPositionToEmployee = async ({ employee, position, accountID, allocation }) => {
    try {
      const matchedVacancy = allocation ?? null;
      const payload = {
        employee: employee.id,
        position: position.id,
        commitment: matchedVacancy === null ? employee.availability : 1,
        allocation: matchedVacancy === null ? employee.availability : matchedVacancy,
        effective_on: position.effective_on,
        notify: position.isNotify,
      };
      if (position.effective_to) payload.effective_to = position.effective_to;
      await addPositionAssignment(accountID, payload);
      return { success: true };
    } catch (e) {
      console.error("error: ", e);
      return { success: false, error: e };
    }
  };
  const onAssignPreparePositionToEmployee = async ({
    employee,
    position,
    effective_date,
    accountID,
    isNotify,
  }) => {
    try {
      const payload = {
        employee: employee.id,
        position: position.id,
        effective_on: effective_date,
        commitment: 3,
        notify: isNotify,
      };
      await addPositionAssignment(accountID, payload);
    } catch (e) {
      console.error("error: ", e);
    }
  };
  const onApplyDatePrepareFor = async ({ employee, effective_date }) => {
    const accountID = employeeStore.currentEmployee.account;
    try {
      const payload = {
        employee: employee.id,
        position: employee?.preparing_for_positions?.[0]?.position,
        effective_on: effective_date,
        commitment: 3,
      };
      const assignment_id = employee?.preparing_for_positions?.[0]?.id;
      await updatePositionAssignment(accountID, payload, assignment_id);
    } catch (e) {
      console.error("error: ", e);
    }
  };
  const onApplyDatePrimaryAssignment = async ({ employee, effective_date }) => {
    const accountID = employeeStore.currentEmployee.account;
    try {
      const payload = {
        employee: employee.id,
        position: employee?.primary_position?.position,
        effective_on: effective_date,
        commitment: 1,
      };
      const assignment_id = employee?.primary_position?.id;
      await updatePositionAssignment(accountID, payload, assignment_id);
    } catch (e) {
      console.error("error: ", e);
    }
  };

  const removePositionAssignmentToEmployee = async ({ accountId, assignment_id, data }) => {
    try {
      await removePositionAssignmentForEmployee(accountId, assignment_id, data);
    } catch (e) {
      console.error("error: ", e);
      throw e;
    }
  };

  const terminate = async ({ accountId, employee, data }) => {
    const payload = {
      effective_by: data,
    };
    const { id } = employee;
    if (id) {
      await terminateEmployee(accountId, id, payload);
    }
  };

  const updateEmployeeInList = (employeeId, patch) => {
    employeeStore.updateEmployeeInList(employeeId, patch);
  };

  const removeEmployeeFromList = (employeeId) => {
    employeeStore.removeEmployeeFromList(employeeId);
  };
  const reactivate = async ({ accountId, employee }) => {
    try {
      const { id } = employee;
      if (id) {
        await reactivateEmployee(accountId, id);
      }
    } catch (e) {
      console.log("error: ", e);
    }
  };
  const activate = async ({ accountId, employee }) => {
    try {
      const { id } = employee;
      if (id) {
        await activateEmployee(accountId, id);
      }
    } catch (e) {
      console.log("e: ", e);
    }
  };
  const deleteEmp = async ({ accountId, employee }) => {
    try {
      const { id } = employee;
      if (id) {
        await deleteEmployee(accountId, id);
      }
    } catch (e) {
      console.log("e: ", e);
    }
  };
  const archiveEmp = async ({ accountId, employee }) => {
    try {
      const { id } = employee;
      if (id) {
        await archiveEmployee(accountId, id);
      }
    } catch (e) {
      console.log("e: ", e);
    }
  };
  const unArchiveEmp = async ({ accountId, employee }) => {
    try {
      const { id } = employee;
      if (id) {
        await unArchiveEmployee(accountId, id);
      }
    } catch (e) {
      console.log("e: ", e);
    }
  };
  const employeeChatter = async (accountID, type, page) => {
    try {
      const { id } = employeeStore.currentEmployee;
      if (id) {
        const response = await getChatterList(accountID, id, type, page);
        return response;
      }
    } catch (e) {
      console.log("e: ", e);
    }
  };
  const getAccountDetails = async (accountID) => {
    try {
      const response = await getAccountData(accountID, { fresh: true });
      return response;
    } catch (e) {
      console.log("e: ", e);
    }
  };
  const updateEmployeeExcludeFromAvg = async (accountID, data) => {
    try {
      const payload = {
        exclude_from_avg: data.value,
      };
      await updateEmployeeExcludeValue(accountID, data.emp_id, payload);
    } catch (error) {
      console.error("error: ", error);
    }
  };

  const getEmployeesCount = () => {
    return employeeStore.getTotalEmployeesCount;
  };

  const updateAssignment = async (accountId, data, id) => {
    try {
      await updatePositionAssignment(accountId, data, id);
    } catch (e) {
      console.error("error: ", e);
    }
  };

  return {
    eligiblePositions,
    eligiblePositionsForPrepare,
    onAssignPositionToEmployee,
    onAssignPreparePositionToEmployee,
    removePositionAssignmentToEmployee,
    allEmployees,
    loadAllEmployees,
    getCurrentEmployee,
    onUpdateAvailability,
    setCurrentEmployee,
    terminate,
    reactivate,
    activate,
    deleteEmp,
    archiveEmp,
    unArchiveEmp,
    operations,
    saveEmployee,
    saveEmployeeWithAvatar,
    checkEmailUser,
    giveAccessToEmployee,
    loadAllEmployeesByBatches,
    employeeChatter,
    onApplyDatePrepareFor,
    onApplyDatePrimaryAssignment,
    getAccountDetails,
    updateEmployeeExcludeFromAvg,
    setPaginationDataLimit,
    paginationDefaultParams,
    paginationLimit,
    paginationOffset,
    paginationOrdering,
    setPaginationDataOffset,
    setOrderingDataField,
    addEmployeeFilter,
    removeEmployeeFilter,
    getDynamicEmployeeFilters,
    clearEmployeeDynamicFilters,
    getEmployeesCount,
    getEmployeeById,
    getEmployeeAssignmentsByEmployeeId,
    updateAssignment,
    updateImportanceValue,
    updateEmployeeInList,
    removeEmployeeFromList,
  };
}
